/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { InactivityHandler } from "@components/InactivityHandler";
import { useEffect, useRef, useState } from "react";
import { useLazyGetPersonByIdQuery } from "@api/personasApi";
import { useLazyGetMetaMapDataQuery } from "@api/metaMapApi";
import { MetaDataJson } from "@interfaces/catalogs";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import { IDENTITY_VALIDATION_NUMBER_OF_ATTEMPTS, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { setParamsIdentityValidationForm } from "@store/slices/bofuSlice";

const ValidacionIdentidadPersona = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const metamapButtonRef = useRef<HTMLDivElement>(null);

  const applicationData = useSelector((state: RootState) => state.register.application);
  const identityValidationForm = useSelector((state: RootState) => state.bofu.params_identity_validation_form);

  const [getDataMetaMap] = useLazyGetMetaMapDataQuery();
  const [getPersonById] = useLazyGetPersonByIdQuery();

  const [personName, setPersonName] = useState("");
  const [enableButton, setEnableButton] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [metaDataJson, setMetaDataJson] = useState<MetaDataJson | null>(null);
  const [identityLegend, setIdentityLegend] = useState<string | null>(null);

  const goBack = () => {
    dispatch(
      setParamsIdentityValidationForm({
        person_id: undefined,
        person_type: undefined,
      }),
    );

    if (applicationData.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code) {
      navigate("/producto/creditosimple/avance_expediente_digitalPFAE");
      return;
    }

    if (applicationData.tax_system_type === TAX_SYSTEM_TYPE.PF.code) {
      navigate("/producto/creditosimple/expediente_pf");
      return;
    }

    switch (identityValidationForm.person_type) {
      case "REP_LEGAL":
        navigate("/producto/creditosimple/docs_representante");
        break;
      default:
        navigate("/producto/creditosimple/docs_aval");
        break;
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const personId = identityValidationForm.person_id;
        let legend = "";

        switch (identityValidationForm.person_type) {
          case "REP_LEGAL":
            legend =
              "En caso de no ser tú el representante legal, podrás reenviar la solicitud de validación vía correo al iniciar el proceso.";
            break;
          case "AVAL":
            legend =
              "En caso de no ser tú el Aval de la Empresa, podrás reenviar la solicitud de validación vía correo al iniciar el proceso.";
            break;
          case "CONYUGE_AVAL":
            legend =
              "En caso de no ser tú el Conyuge del Aval, podrás reenviar la solicitud de validación vía correo al iniciar el proceso.";
            break;
          case "AVAL_ADICIONAL":
            legend =
              "En caso de no ser tú el Aval Adicional, podrás reenviar la solicitud de validación vía correo al iniciar el proceso.";
            break;
          case "CONYUGE_AVAL_ADICIONAL":
            legend =
              "En caso de no ser tú el Conyuge del Aval Adicional, podrás reenviar la solicitud de validación vía correo al iniciar el proceso.";
            break;
          case "REP_LEGAL_PF":
            legend =
              "En caso de no ser tú el representante legal, podrás reenviar la solicitud de validación vía correo al iniciar el proceso.";
            break;
          default:
            legend = "";
            break;
        }

        if (!personId || !legend) {
          console.error("No hay persona seleccionada");
          goBack();
          return;
        }

        const person_selected = await getPersonById(personId).unwrap();

        if (!person_selected) {
          console.error("No se encontró la persona seleccionada");
          goBack();
          return;
        }

        if (person_selected.identity_validation_completed) {
          console.error("La persona ya tiene la validación de identidad completada");
          goBack();
          return;
        }

        const metamap_res = await getDataMetaMap(person_selected.id).unwrap();
        const metamap_info = metamap_res?.data;

        if (metamap_info.attempts > IDENTITY_VALIDATION_NUMBER_OF_ATTEMPTS) {
          console.error("El número de intentos de validación de identidad ha sido excedido");
          goBack();
          return;
        }

        setMetaDataJson({
          client_details_id: metamap_info.client_details_id,
          loan_application_id: metamap_info.loan_application_id,
          person_id: metamap_info.person_id,
          person_type: metamap_info.person_type,
          regimen: metamap_info.regimen,
          attempts: metamap_info.attempts,
          upload_type: metamap_info.upload_type,
        });

        setIdentityLegend(legend);
        setPersonName([person_selected.name, person_selected.last_name, person_selected.last_name_2].join(" ").trim());
        setEnableButton(true);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "ValidacionIdentidadPersona-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleUserFinishedSdk = (_event: CustomEvent) => {
      setMetaDataJson(null);
      setEnableButton(false);
      goBack();
    };

    const metamapButton = metamapButtonRef.current;

    if (metamapButton) {
      metamapButton.addEventListener("metamap:userFinishedSdk", handleUserFinishedSdk as EventListener);
    }

    return () => {
      if (metamapButton) {
        metamapButton.removeEventListener("metamap:userFinishedSdk", handleUserFinishedSdk as EventListener);
      }
    };
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box textAlign={"center"} mx={4} mt={4}>
          <Typography
            textAlign={"center"}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Validación identidad
          </Typography>

          {isLoading ? (
            <Box mt={3}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
            </Box>
          ) : (
            <Box mt={3}>
              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.main}
                mt={4}
              >
                {personName || ""}
              </Typography>

              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={600}
                color={theme?.palette?.primary?.dark}
                mt={4}
              >
                Validaremos tu identidad por medio de fotografías faciales y tu identificación oficial
              </Typography>

              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.dark}
                mt={4}
              >
                {identityLegend}
              </Typography>

              <Box mt={4} sx={{ pointerEvents: isLoading ? "none" : "auto", opacity: isLoading ? 0.5 : 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    pointerEvents: !enableButton ? "none" : "auto",
                    opacity: !enableButton ? 0.5 : 1,
                    cursor: !enableButton ? "not-allowed" : "pointer",
                  }}
                >
                  <metamap-button
                    ref={metamapButtonRef}
                    clientid={import.meta.env.VITE_META_CLIENT_ID}
                    flowId={import.meta.env.VITE_META_FLOW_ID}
                    merchantToken={import.meta.env.VITE_MERCHANT_TOKEN}
                    language="es"
                    metadata={JSON.stringify(metaDataJson || {})}
                    color={theme?.palette?.primary?.main}
                  />
                </Box>
              </Box>

              <Box display="flex" justifyContent="center" mt={4}>
                <Button variant="blue_outlined" type="button" onClick={goBack}>
                  Regresar al expediente
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default ValidacionIdentidadPersona;
